import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

interface Account {
  id: string;
  name: string;
  created_at: string;
  user_count: number;
  report_count: number;
  archived_at?: string | null;
}


interface UserAccount {
  id: string;
  name: string;
}

interface UserRecord {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_superuser: boolean;
  accounts: UserAccount[];
  date_joined: string;
  last_login: string | null;
}

export default function AccountsPage() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Accounts</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: '1px solid #D7E1E8' }}>
        <Tab label="Accounts" />
        <Tab label="Users" />
      </Tabs>
      {tab === 0 && <AccountsTab />}
      {tab === 1 && <UsersTab />}
    </Box>
  );
}

// ── Accounts Tab ──

function AccountsTab() {
  const { refreshAccounts } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<Account | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const restore = async (a: Account) => {
    setError('');
    try {
      await api.post(`/accounts/${a.id}/restore/`);
      load();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to restore account.');
    }
  };
  const [archiveError, setArchiveError] = useState('');
  const [archiveSaving, setArchiveSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/accounts/', { params: showArchived ? { include_archived: 'true' } : {} })
      .then(r => setAccounts(r.data)).finally(() => setLoading(false));
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setName(''); setError(''); setDialogOpen(true); };
  const openEdit = (a: Account) => { setEditing(a); setName(a.name); setError(''); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = { name };
      if (editing) {
        await api.put(`/accounts/${editing.id}/`, payload);
      } else {
        await api.post('/accounts/', payload);
      }
      setDialogOpen(false);
      load();
      refreshAccounts();
    } catch (err: any) {
      setError(err.response?.data?.name?.[0] || err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const openArchive = (a: Account) => {
    setArchiveTarget(a);
    setArchiveError('');
  };

  const closeArchive = () => {
    if (archiveSaving) return;
    setArchiveTarget(null);
    setArchiveError('');
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    setArchiveSaving(true);
    setArchiveError('');
    try {
      await api.delete(`/accounts/${archiveTarget.id}/`);
      setArchiveTarget(null);
      load();
      refreshAccounts();
    } catch (err: any) {
      setArchiveError(err.response?.data?.error || 'Failed to archive account.');
    } finally {
      setArchiveSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  const q = search.trim().toLowerCase();
  const filtered = q ? accounts.filter(a => a.name.toLowerCase().includes(q)) : accounts;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search accounts…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button size="small" variant={showArchived ? 'outlined' : 'text'} onClick={() => setShowArchived((v) => !v)} sx={{ mr: 1 }}>
          {showArchived ? 'Hide archived' : 'Show archived'}
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New Account</Button>
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Users</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Reports</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(a => (
              <TableRow
                key={a.id}
                sx={{
                  borderTop: '1px solid #E5E7EB',
                  '&:hover': { bgcolor: '#F9FAFB' },
                  '& td': { borderBottom: 'none' },
                }}
              >
                <TableCell><Typography fontWeight={600}>{a.name}</Typography></TableCell>
                <TableCell align="center">{a.user_count}</TableCell>
                <TableCell align="center">{a.report_count}</TableCell>
                <TableCell>{new Date(a.created_at).toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  {a.archived_at ? (
                    <Button size="small" onClick={() => restore(a)}>Restore</Button>
                  ) : (
                    <>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Archive"><IconButton size="small" onClick={() => openArchive(a)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow sx={{ borderTop: '1px solid #E5E7EB' }}>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary', borderBottom: 'none' }}>
                  {accounts.length === 0 ? 'No accounts yet' : `No accounts match "${search}"`}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!archiveTarget} onClose={closeArchive} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Archive account</DialogTitle>
        <DialogContent>
          {archiveError && <Alert severity="error" sx={{ mb: 2 }}>{archiveError}</Alert>}
          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
            Archive <b>{archiveTarget?.name}</b>?
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Its reports will be hidden from normal views but kept, with their files.
            You can restore it later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeArchive} disabled={archiveSaving}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmArchive}
            disabled={archiveSaving}
          >
            {archiveSaving ? <CircularProgress size={18} color="inherit" /> : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit Account' : 'New Account'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            label="Account Name"
            fullWidth
            value={name}
            onChange={e => setName(e.target.value)}
            sx={{ mt: 1 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Users Tab ──

function UsersTab() {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterAccount, setFilterAccount] = useState<string>('');

  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserRecord | null>(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '' });
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState<UserRecord | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = filterAccount ? `?account_id=${filterAccount}` : '';
    Promise.all([
      api.get(`/users/${params}`),
      api.get('/accounts/'),
    ]).then(([u, a]) => {
      setUsers(u.data);
      setAccounts(a.data);
    }).finally(() => setLoading(false));
  }, [filterAccount]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (u: UserRecord) => {
    setEditUser(u);
    setEditForm({ first_name: u.first_name, last_name: u.last_name, email: u.email });
    setEditError('');
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    setEditSaving(true);
    setEditError('');
    try {
      await api.put(`/users/${editUser.id}/`, editForm);
      setEditOpen(false);
      load();
    } catch (err: any) {
      setEditError(err.response?.data?.email?.[0] || err.response?.data?.error || 'Failed to save.');
    } finally {
      setEditSaving(false);
    }
  };

  const openPassword = (u: UserRecord) => {
    setPwUser(u);
    setNewPassword('');
    setPwError('');
    setPwOpen(true);
  };

  const handleSetPassword = async () => {
    if (!pwUser) return;
    setPwSaving(true);
    setPwError('');
    try {
      await api.post(`/users/${pwUser.id}/set-password/`, { password: newPassword });
      setPwOpen(false);
    } catch (err: any) {
      setPwError(err.response?.data?.password?.[0] || 'Failed to set password.');
    } finally {
      setPwSaving(false);
    }
  };

  const toggleActive = async (u: UserRecord) => {
    const action = u.is_active ? 'deactivate' : 'activate';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${u.email}?`)) return;
    try {
      await api.put(`/users/${u.id}/`, { is_active: !u.is_active });
      load();
    } catch {
      alert(`Failed to ${action} user.`);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Account</InputLabel>
          <Select value={filterAccount} label="Filter by Account" onChange={e => setFilterAccount(e.target.value)}>
            <MenuItem value="">All Accounts</MenuItem>
            {accounts.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Accounts</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id}>
                <TableCell>
                  <Typography fontSize="0.875rem" fontWeight={500}>{u.email}</Typography>
                </TableCell>
                <TableCell>{u.first_name} {u.last_name}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {u.accounts.map(a => (
                      <Chip key={a.id} label={a.name} size="small" sx={{ fontSize: '0.75rem' }} />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={u.is_active ? 'Active' : 'Deactivated'}
                    size="small"
                    color={u.is_active ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(u)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Set Password"><IconButton size="small" onClick={() => openPassword(u)}><LockResetIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title={u.is_active ? 'Deactivate' : 'Activate'}>
                    <IconButton size="small" onClick={() => toggleActive(u)}>
                      {u.is_active ? <BlockIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" color="success" />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No users found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <TextField label="First Name" fullWidth value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} sx={{ mt: 1, mb: 2 }} />
          <TextField label="Last Name" fullWidth value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} sx={{ mb: 2 }} />
          <TextField label="Email" fullWidth value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving}>{editSaving ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pwOpen} onClose={() => setPwOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set Password for {pwUser?.email}</DialogTitle>
        <DialogContent>
          {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}
          <TextField label="New Password" type="password" fullWidth value={newPassword} onChange={e => setNewPassword(e.target.value)} sx={{ mt: 1 }} autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSetPassword} disabled={pwSaving || newPassword.length < 8}>{pwSaving ? <CircularProgress size={20} /> : 'Set Password'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
